import { Fragment, useEffect, useState, useMemo } from 'react';
import { MdKeyboardArrowRight } from 'react-icons/md';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Center, HStack, Image, keyframes } from '@chakra-ui/react';
import AppRoutes from 'config/AppRoutes';
import BreadcrumbItem from 'ui/molecules/BreadcrumbItem';
import Button from 'ui/molecules/Button';
import useIsMobileScreen from 'utils/hooks/useIsMobileScreen';
import { isRosselVersion } from 'utils/vendorUtils';
import { useSelector } from 'react-redux';
import { StepParamsSelector } from 'store/selectors';
import { useVendorConfig } from 'store/vendor/useVendorConfig';
import {
  allConditionalSteps,
  getStepNamesAccordingToChoices,
  noBreadcrumbsItems,
} from 'config/steps';
import getCurrentRoute from 'utils/getCurrentRoute';
import { ConditionParams } from 'config/utils/types';
import { useCountryCode } from '../utils/hooks/useCountryCode';

const getCurrentProgress = (
  stepParams: ConditionParams,
  pathName: string
): number => {
  const steps = getStepNamesAccordingToChoices(allConditionalSteps, stepParams);
  const route = getCurrentRoute(pathName);
  return ((steps.indexOf(route) + 1) / steps.length) * 100;
};

const Header = () => {
  const { pathname } = useLocation();
  const countryCode = useCountryCode();
  const { mainUrlSite } = useVendorConfig();
  const { t } = useTranslation();
  const showBreadcrumb =
    pathname !== AppRoutes.ESTIMATE &&
    pathname !== AppRoutes.THANKS &&
    // pathname !== AppRoutes.FLOW_CHOICE &&
    pathname !== AppRoutes.BUSINESS_CHOICE;
  const isMobile = useIsMobileScreen();
  const stepParams = useSelector(StepParamsSelector);
  const steps = useMemo(
    () => getStepNamesAccordingToChoices(allConditionalSteps, stepParams),
    [stepParams]
  );

  const [previousPercent, setPreviousPercent] = useState<number>(0);
  const [currentPercent, setCurrentPercent] = useState<number>(0);

  const breadcrumbItems = steps
    .filter((x) => !noBreadcrumbsItems.includes(x))
    .map((value) => {
      const elem = Object.entries(AppRoutes).find(
        ([_key, val]) => val === value
      );
      return elem;
    });

  const progress = keyframes`
    from {width: ${previousPercent}%;}
    to {width: ${currentPercent}%;}
  `;

  // --------------------- methods ------------------------

  const isStepAccessible = (step: string) => {
    // edit: we can only go back with breadcrumb to avoid issues
    const currentIndex = breadcrumbItems.findIndex(
      ([_key, value]) => value === getCurrentRoute(pathname)
    );
    const stepIndex = breadcrumbItems.findIndex(
      ([_key, value]) => value === step
    );

    if (currentIndex > -1 && stepIndex > -1 && stepIndex < currentIndex)
      return true;
    return false;
  };

  const handleBackToSite = () => {
    window.location.href = `${mainUrlSite}/${countryCode}`;
  };

  // --------------------- effects ------------------------

  useEffect(() => {
    const newPercent = getCurrentProgress(stepParams, pathname);
    setCurrentPercent(newPercent);
    return () => {
      setPreviousPercent(newPercent);
    };
  }, [pathname, stepParams]);

  // --------------------- render ------------------------

  return (
    <Box w="full" color="reno.white">
      <HStack
        w="full"
        h="68px"
        p="10px"
        boxShadow="base"
        position="relative" // needed to get the shadow appear over next div
      >
        <Center w="full" style={{}} cursor="auto">
          <Image
            maxH="50px"
            src={`vendor/${process.env.REACT_APP_VENDOR}/logo.svg`}
            alt="reno-solutions logo"
          />
        </Center>
        {/* {stepParams.flowType === 'b2b' && (
          <Tag size="lg" variant="solid" bg="primaryColor_active">
            B2B
          </Tag>
        )} */}

        <Button
          position="absolute"
          left="0"
          visibility={isRosselVersion ? 'hidden' : 'visible'}
          padding={{ sm: '2px', md: '10px' }}
          fontSize={{ sm: '14px', md: '16px' }}
          fontWeight="normal"
          variant="backButton"
          onClick={handleBackToSite}
        >
          {t(isMobile ? 'header.back_to_site.small' : 'header.back_to_site')}
        </Button>
      </HStack>

      <Box w="full">
        <Box
          bgColor="secondaryColor"
          height="5px"
          transformOrigin="0"
          animation={`${progress} forwards 0.3s ease-out`}
        />
      </Box>

      {showBreadcrumb && (
        <HStack
          bgColor="gray.50"
          color="gray.400"
          className="noselect"
          p="5px 20px"
          overflow="hidden"
          justifyContent="left"
        >
          {breadcrumbItems.map(([key, value], i) => {
            const isSelected = value === getCurrentRoute(pathname);
            return (
              <Fragment key={key}>
                <BreadcrumbItem
                  key={key}
                  path={value}
                  disabled={!isStepAccessible(value)}
                  label={
                    i === 0
                      ? stepParams.flowType?.toUpperCase()
                      : `${i + 1}. ${t(`${key}_menu`)}`
                  }
                  selected={isSelected}
                />
                {i < breadcrumbItems.length - 1 && (
                  <Box>
                    <MdKeyboardArrowRight color="black" />
                  </Box>
                )}
              </Fragment>
            );
          })}
        </HStack>
      )}
    </Box>
  );
};

export default Header;
